/**
 * The "there is a new version" button.
 *
 * Lives in the top bar on every screen, and only exists when there is
 * actually something to install - an update control that is always visible is
 * furniture people stop seeing. The first time it appears it says so out
 * loud, with a callout pointing at it, and then it shuts up and stays a small
 * quiet button until you press it.
 *
 * It knows the difference between downloaded and not: an update still coming
 * down shows its progress, and only turns into "Restart" once the bytes are
 * on disk and pressing it will actually do something.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bridge, type UpdateState } from '@/lib/bridge';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

/** Which version we have already introduced, so it is announced once. */
const SEEN_KEY = 'pogu.updateSeen';

export function UpdatePill() {
  const [state, setState] = useState<UpdateState | null>(null);
  const [callout, setCallout] = useState(false);
  const [busy, setBusy] = useState(false);
  const pill = useRef<HTMLButtonElement>(null);

  /* Follow the updater. */
  useEffect(() => {
    const b = bridge();
    if (!b?.update) return;

    void b.update.state().then(setState).catch(() => {});
    return b.update.onState(setState);
  }, []);

  const status = state?.status;
  const showing = status === 'available' || status === 'downloading' || status === 'ready';

  /*
   * Introduce a version once.
   *
   * Announced on the first appearance of a given version number and never
   * again for that one - so restarting the app all evening does not mean
   * being told the same thing all evening.
   */
  useEffect(() => {
    if (!showing || !state?.latest) return;

    let seen: string | null = null;
    try { seen = localStorage.getItem(SEEN_KEY); } catch { /* storage off */ }
    if (seen === state.latest) return;

    // A beat after the app settles, so it is not competing with everything
    // else that renders on launch.
    const t = window.setTimeout(() => setCallout(true), 1400);
    return () => window.clearTimeout(t);
  }, [showing, state?.latest]);

  function dismiss() {
    setCallout(false);
    try { if (state?.latest) localStorage.setItem(SEEN_KEY, state.latest); } catch { /* storage off */ }
  }

  async function act() {
    const b = bridge();
    if (!b?.update || busy) return;
    dismiss();
    setBusy(true);
    try {
      if (status === 'ready') await b.update.install();
      else await b.update.download();
    } catch { /* the state stream reports what happened */ } finally {
      setBusy(false);
    }
  }

  if (!showing) return null;

  const ready = status === 'ready';
  const downloading = status === 'downloading';
  const pct = Math.round((state?.progress ?? 0) * 100);

  return (
    <>
      <button
        ref={pill}
        type="button"
        onClick={() => void act()}
        title={ready ? 'Restart to finish updating' : 'Version ' + (state?.latest ?? '') + ' is available'}
        className={cn(
          'relative flex h-[30px] flex-none items-center gap-1.5 overflow-hidden rounded-full px-3',
          'text-[12px] font-bold transition-[background-color,box-shadow] hover:bg-accent-hi',
          ready
            ? 'bg-good text-[var(--bg-0)]'
            : 'bg-accent-soft text-accent-hi'
        )}
      >
        {/* Fills up as it downloads, so the button is its own progress bar. */}
        {downloading ? (
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 bg-[color-mix(in_srgb,var(--accent)_30%,transparent)] transition-[width] duration-200"
            style={{ width: pct + '%' }}
          />
        ) : null}

        <span className="relative flex items-center gap-1.5">
          <Icon name={ready ? 'bolt' : 'download'} size={13} className={cn(!ready && !downloading && 'animate-pulse')} />
          {downloading ? pct + '%' : ready ? 'Restart to update' : 'Update'}
        </span>
      </button>

      {callout ? (
        <Callout
          anchor={pill.current}
          version={state?.latest ?? ''}
          notes={state?.notes ?? []}
          ready={ready}
          onAct={() => void act()}
          onClose={dismiss}
        />
      ) : null}
    </>
  );
}

/**
 * The thing that points at the button.
 *
 * Anchored to the real element rather than parked in a corner, because the
 * whole job is teaching you where the button is. It closes on its own after
 * a while - a callout that waits forever is a dialog.
 */
function Callout({ anchor, version, notes, ready, onAct, onClose }: {
  anchor: HTMLElement | null;
  version: string;
  notes: string[];
  ready: boolean;
  onAct: () => void;
  onClose: () => void;
}) {
  const [spot, setSpot] = useState<{ left: number; top: number; arrow: number } | null>(null);

  useEffect(() => {
    const place = () => {
      const a = anchor?.getBoundingClientRect();
      if (!a) return;

      const width = 300;
      const centre = a.left + a.width / 2;
      const left = Math.max(12, Math.min(centre - width / 2, window.innerWidth - width - 12));
      setSpot({ left, top: a.bottom + 12, arrow: centre - left });
    };

    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor]);

  /* Says its piece and goes. */
  useEffect(() => {
    const t = window.setTimeout(onClose, 12_000);
    return () => window.clearTimeout(t);
  }, [onClose]);

  if (!spot) return null;

  return createPortal(
    <>
      {/* Clicking anywhere else puts it away, without blocking what you clicked. */}
      <span className="fixed inset-0 z-[440]" onClick={onClose} />

      <div
        style={{ left: spot.left, top: spot.top, width: 300 }}
        className="fixed z-[450] animate-[callout-in_260ms_var(--ease-out-soft)] rounded-[calc(14px*var(--radius-mul,1))] border border-accent-line bg-surface-2 p-3.5 shadow-[var(--shadow-2)]"
      >
        {/* The arrow, lined up with the middle of the button. */}
        <span
          aria-hidden
          style={{ left: Math.max(14, Math.min(spot.arrow, 300 - 14)) }}
          className="absolute -top-[7px] -ml-[7px] h-[13px] w-[13px] rotate-45 rounded-[calc(3px*var(--radius-mul,1))] border-t border-l border-accent-line bg-surface-2"
        />

        <div className="flex items-start gap-2.5">
          <span className="grid h-8 w-8 flex-none place-items-center rounded-[calc(10px*var(--radius-mul,1))] bg-accent-soft text-accent-hi">
            <Icon name="sparkle" size={16} />
          </span>

          <div className="min-w-0 flex-1">
            <h3 className="text-[13.5px] font-bold">
              {ready ? 'Update ready to install' : 'Pugo ' + version + ' is out'}
            </h3>
            <p className="mt-0.5 text-[12px] leading-snug text-mute">
              {ready
                ? 'Restart and it takes a few seconds. Your call keeps running until you do.'
                : 'Press the button up here whenever suits you - nothing restarts on its own.'}
            </p>

            {/* What changed, when the release bothered to say. */}
            {notes.length ? (
              <ul className="mt-2 flex flex-col gap-1">
                {notes.slice(0, 3).map((n, i) => (
                  <li key={i} className="flex gap-1.5 text-[11.5px] leading-snug text-dim">
                    <span className="mt-[6px] h-1 w-1 flex-none rounded-full bg-accent" />
                    <span className="min-w-0">{n}</span>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="mt-2.5 flex items-center gap-2">
              <button
                type="button"
                onClick={onAct}
                className="h-[28px] rounded-[calc(8px*var(--radius-mul,1))] bg-accent px-3 text-[12px] font-bold text-accent-fg transition-[background-color] hover:bg-accent-hi"
              >
                {ready ? 'Restart now' : 'Download it'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="h-[28px] rounded-[calc(8px*var(--radius-mul,1))] px-2.5 text-[12px] font-semibold text-mute transition-colors hover:text-dim"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
